import {
	ProctoringAssessmentConfigurationV2,
	ProctoringOption,
	Application,
	OptionRelatedInstituteSettings,
	Extension,
} from "@verity/proctoring-option/types";
import { DEFAULT_PROCTORING_CONFIGURATION_OPTIONS, enforceConfiguration } from "@verity/proctoring-option";
import { VerityApi, VerityApiContext } from "../../api/fetch";
import { useContext, useEffect, useRef, useState } from "react";
import { PendingLiveSession, spansMultipleDays } from "../liveHelper";
import { VerityInstituteSettings } from "../../types/common";
import {
	ConfigurationPageName,
	getPlatformTextFromProctoringOptions,
	PageIndexNumber,
	StandaloneConfigurationPageName,
} from "../proctoring-options/option-text";
import { useMobileLayout } from "../../hooks";
import { categorizeProctoringOptions, ConfigurationSummaryList } from "../proctoring-options/configurationSummaryList";
import { CoreButton, CorePaper, CoreSpinner } from "../../core";
import i18nPackage from "../../i18n/i18n";
import IncompleteIcon from "../images/incomplete-icon.svg?react";
import { ConditionalWrapper } from "../../wrapper";
import { FormControlLabel, Tooltip, Typography } from "@mui/material";
import styles from "./confirmation-step.module.css";
import { UnsavedAssessmentInfo } from "../editQuizModalWrapper";

const useApiInstituteSettings = (instituteUuid?: string, getInstituteSettings?: VerityApi["getInstituteSettings"]) => {
	const [apiInstituteSettings, setApiInstituteSettings] = useState<
		Awaited<ReturnType<VerityApi["getInstituteSettings"]>> | undefined
	>(undefined);
	useEffect(() => {
		if (instituteUuid && getInstituteSettings) {
			void getInstituteSettings(instituteUuid).then((res) => {
				setApiInstituteSettings(res);
			});
		}
	}, [instituteUuid, getInstituteSettings]);
	return [apiInstituteSettings];
};

type ConfirmationPageProps = {
	proctoringAssessmentConfiguration: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
	name?: string;
	option: Partial<ProctoringOption>;
	quizInfo: UnsavedAssessmentInfo;
	exemptionCount: number;
	pendingLiveSessions: PendingLiveSession[];
	loading?: boolean;
	setCurPageValue: React.Dispatch<React.SetStateAction<number>>;
	enforcedProfilesOnly: boolean;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings: VerityInstituteSettings;
	totalPageNames: (ConfigurationPageName | StandaloneConfigurationPageName)[];
	isStandaloneQuizFlow: boolean;
	applications: Application[];
	extensions: Extension[];
	instituteUuid?: string;
	getInstituteSettings?: VerityApi["getInstituteSettings"];
	setThirdPartySaveEnabled?: (enabled: boolean) => void;
	setSelectedPlatformId?: (platform: number | undefined | null) => void;
};

export default function ConfirmationPage({
	proctoringAssessmentConfiguration,
	name,
	option,
	quizInfo,
	exemptionCount,
	pendingLiveSessions,
	loading,
	setCurPageValue,
	enforcedProfilesOnly,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	totalPageNames,
	isStandaloneQuizFlow,
	applications,
	extensions,
	instituteUuid,
	getInstituteSettings,
	setThirdPartySaveEnabled,
	setSelectedPlatformId,
}: ConfirmationPageProps) {
	const { getCourseStudents, getProctoringExemptionStudents, getExemptionInfoSuccessStatus } =
		useContext(VerityApiContext);
	const [studentFetchPending, setStudentFetchPending] = useState(false);
	const isMobileLayout = useMobileLayout();

	const maxRetries = 2;
	const exemptionModalClosed = useRef(false);

	const [apiInstituteSettings] = useApiInstituteSettings(instituteUuid, getInstituteSettings);
	const { optionCategories, optionChildren } = categorizeProctoringOptions(
		option,
		verityInstituteSettings,
		optionRelatedInstituteSettings,
		apiInstituteSettings,
	);

	useEffect(() => {
		async function fetchInitialExemptions() {
			const success = await toggleExemptionModal();
			if (!success) {
				void handleRetry();
			}
		}

		async function handleRetry() {
			const { quizId } = quizInfo;

			if (!quizId) {
				return;
			}

			const exemptStatus = await getExemptionInfoSuccessStatus({ ...quizInfo, quizId });
			if (exemptStatus) {
				for (let i = 0; i < maxRetries; i++) {
					if (exemptionModalClosed.current) {
						exemptionModalClosed.current = false;
						return;
					}
					const retrySuccess = await toggleExemptionModal();
					if (retrySuccess) {
						return;
					}
				}
			}
		}

		void fetchInitialExemptions();
	}, []);

	useEffect(() => {
		if (!setSelectedPlatformId || !setThirdPartySaveEnabled || !proctoringAssessmentConfiguration) return;
		if (
			proctoringAssessmentConfiguration.thirdPartyAssessmentOption &&
			!proctoringAssessmentConfiguration.thirdPartyAssessmentOption?.thirdPartyPlatformId
		) {
			setSelectedPlatformId(0);
		}
		setThirdPartySaveEnabled(true);
	}, []);

	async function toggleExemptionModal() {
		const { quizId, courseId } = quizInfo;

		if (!quizId || !courseId) {
			console.error("Quiz ID or Course ID is missing, cannot fetch exemption info");
			return;
		}

		try {
			if (studentFetchPending) {
				return;
			}
			setStudentFetchPending(true);
			let studentRoster;
			let exemptedUserIds;

			if (Object.keys(quizInfo).includes("standaloneInfo")) {
				[studentRoster, exemptedUserIds] = await Promise.all([
					getCourseStudents(quizInfo),
					getProctoringExemptionStudents({ ...quizInfo, quizId }),
				]);
			} else {
				[studentRoster, exemptedUserIds] = await Promise.all([
					getCourseStudents(quizInfo),
					getProctoringExemptionStudents({ ...quizInfo, quizId }),
				]);
			}

			if (!studentRoster || !exemptedUserIds) {
				setStudentFetchPending(false);
				return false;
			}
			setStudentFetchPending(false);
		} catch (err) {
			console.error(err);
			setStudentFetchPending(false);
			return false;
		}
	}

	function formatLiveSessionDate(date: Date) {
		return date.toLocaleDateString([], { dateStyle: "long" });
	}

	function formatLiveSessionTime(time: Date) {
		return time.toLocaleTimeString([], { timeStyle: "short" });
	}

	let proctoringMethod: string;
	if (proctoringAssessmentConfiguration.isLiveProctored) {
		proctoringMethod = "Live Proctoring";
	} else if (proctoringAssessmentConfiguration.isLivePopIn) {
		if (proctoringAssessmentConfiguration.enablePostReview) {
			proctoringMethod = "Live Check-In with Post-Assessment Review";
		} else {
			proctoringMethod = "Live Check-In";
		}
	} else {
		proctoringMethod = "Automated Proctoring";
	}

	const EditButton = ({
		targetPageName,
		label = "Edit",
	}: {
		targetPageName: ConfigurationPageName | StandaloneConfigurationPageName;
		/**Text team wants live session step to be modify instead of edit */
		label?: "Edit" | "Modify";
	}) => {
		const pageIndex = totalPageNames.indexOf(targetPageName) as PageIndexNumber;
		const labelKey =
			label === "Edit"
				? "editQuizPage.confirmationStep.editButtonLabel"
				: "editQuizPage.confirmationStep.modifyButtonLabel";

		return (
			<CoreButton
				onClick={() => setCurPageValue(pageIndex)}
				aria-label={i18nPackage.t("editQuizPage.confirmationStep.editButtonAriaLabel", {
					page: targetPageName.toLowerCase(),
				})}
			>
				{i18nPackage.t(labelKey)}
			</CoreButton>
		);
	};

	function LiveSessionWarningText() {
		// If user toggled live session but did not create live session (can happen now because stepepr is non linear, so user can jump steps)
		if (proctoringAssessmentConfiguration.isLiveProctored && pendingLiveSessions.length === 0) {
			return (
				<Typography component={"span"} className={styles.liveSessionWarning}>
					<IncompleteIcon />
					<span>{i18nPackage.t("editQuizPage.confirmationStep.liveSession.incompleteLabel")}</span>
				</Typography>
			);
		}
		return null;
	}

	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper component="section" className={styles.confirmationPage}>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => <div>{children}</div>}
		>
			{loading ? (
				<div className={styles.loadingContainer}>
					<CoreSpinner size={100} />
				</div>
			) : (
				<ConditionalWrapper
					condition={!isMobileLayout}
					whenTrue={(children) => <div className={styles.confirmationBody}>{children}</div>}
				>
					{proctoringAssessmentConfiguration.thirdPartyAssessmentOption && (
						<div
							style={{
								width: isMobileLayout ? "100%" : undefined,
								maxWidth: isMobileLayout ? "100%" : undefined,
							}}
						>
							<div
								className={styles.confirmationRow}
								style={{
									alignItems: isMobileLayout ? "end" : undefined,
								}}
							>
								<Typography
									className={styles.sectionTitle}
									style={{
										flexBasis: isMobileLayout ? undefined : "35%",
									}}
								>
									{i18nPackage.t("editQuizPage.confirmationStep.thirdPartyAssessmentDetails")}
								</Typography>
								<div style={{ alignItems: "start" }}>
									<EditButton targetPageName="Third-Party"></EditButton>
								</div>
							</div>

							<hr className={styles.divider} />
							<div className={styles.thirdPartyBody}>
								{name && (
									<FormControlLabel
										className={`${styles.thirdPartyInput} ${isMobileLayout ? styles.thirdPartyInputCompactLabel : ""}`}
										labelPlacement="start"
										control={
											<Tooltip title={name}>
												<Typography variant="body1" className={styles.thirdBodyHeader}>
													{name}
												</Typography>
											</Tooltip>
										}
										componentsProps={{
											typography: {
												style: {
													fontSize: "15px",
													width: isMobileLayout ? "100%" : undefined,
													maxWidth: isMobileLayout ? "135px" : undefined,
													minWidth: isMobileLayout ? "135px" : undefined,
												},
											},
										}}
										label={i18nPackage.t("editQuizPage.confirmationStep.assessmentName")}
									/>
								)}
								<FormControlLabel
									className={`${styles.thirdPartyInput} ${isMobileLayout ? styles.thirdPartyInputCompactLabel : ""}`}
									labelPlacement="start"
									control={
										<Tooltip title={proctoringAssessmentConfiguration.thirdPartyAssessmentOption.thirdPartyUrl}>
											<Typography variant="body1" className={styles.thirdBodyHeader}>
												{proctoringAssessmentConfiguration.thirdPartyAssessmentOption.thirdPartyUrl}
											</Typography>
										</Tooltip>
									}
									componentsProps={{
										typography: {
											style: {
												fontSize: "15px",
												width: isMobileLayout ? "100%" : undefined,
												maxWidth: isMobileLayout ? "135px" : undefined,
												minWidth: isMobileLayout ? "135px" : undefined,
											},
										},
									}}
									label={i18nPackage.t("editQuizPage.confirmationStep.assessmentUrl")}
								/>
								{proctoringAssessmentConfiguration.thirdPartyAssessmentOption.password && (
									<FormControlLabel
										className={`${styles.thirdPartyInput} ${isMobileLayout ? styles.thirdPartyInputCompactLabel : ""}`}
										labelPlacement="start"
										control={
											<Tooltip title={proctoringAssessmentConfiguration.thirdPartyAssessmentOption.password}>
												<Typography variant="body1" className={styles.thirdBodyHeader}>
													{proctoringAssessmentConfiguration.thirdPartyAssessmentOption.password}
												</Typography>
											</Tooltip>
										}
										componentsProps={{
											typography: {
												style: {
													fontSize: "15px",
													width: isMobileLayout ? "100%" : undefined,
													maxWidth: isMobileLayout ? "135px" : undefined,
													minWidth: isMobileLayout ? "135px" : undefined,
												},
											},
										}}
										label={i18nPackage.t("editQuizPage.confirmationStep.assessmentPassword")}
									/>
								)}
							</div>
						</div>
					)}
					{!isStandaloneQuizFlow && totalPageNames.includes("Method") && (
						<div
							style={{
								width: isMobileLayout ? "100%" : undefined,
								maxWidth: isMobileLayout ? "100%" : undefined,
							}}
						>
							<div
								className={styles.confirmationRow}
								style={{
									alignItems: isMobileLayout ? "end" : undefined,
								}}
							>
								<Typography
									style={{
										flexBasis: isMobileLayout ? undefined : "35%",

										maxWidth: "200px",
									}}
									className={styles.sectionTitle}
								>
									{i18nPackage.t("editQuizPage.confirmationStep.method")}
								</Typography>
								{!enforcedProfilesOnly && (
									<div style={{ alignItems: "start" }}>
										<EditButton targetPageName="Method"></EditButton>
									</div>
								)}
							</div>

							<hr className={styles.divider} />
							<div style={{ marginTop: "12px" }}>
								<div className={styles.confirmationPageDetail} style={{ marginTop: "10px" }}>
									{proctoringMethod}
								</div>
							</div>
						</div>
					)}

					{proctoringAssessmentConfiguration.isLiveProctored && (
						<div
							style={{
								width: isMobileLayout ? "100%" : undefined,
								maxWidth: isMobileLayout ? "100%" : undefined,
							}}
						>
							<div
								className={styles.confirmationRow}
								style={{
									alignItems: isMobileLayout ? "end" : undefined,
								}}
							>
								<div className={styles.liveSectionTitleWrapper}>
									{i18nPackage.t("editQuizPage.confirmationStep.liveSession.title")}
									<LiveSessionWarningText />
								</div>
								<div style={{ alignItems: "start" }}>
									<EditButton targetPageName="Live Sessions" label="Modify"></EditButton>
								</div>
							</div>

							<hr className={styles.divider} />
							<table className={styles.liveSessions}>
								{pendingLiveSessions.map(({ identifier, name, startTime, endTime }) => {
									const multipleDates = spansMultipleDays(startTime, endTime);
									return (
										<tr key={identifier}>
											<th>{name}:</th>
											<td>
												{multipleDates
													? `${formatLiveSessionDate(startTime)} ${formatLiveSessionTime(startTime)} to ${formatLiveSessionDate(endTime)} ${formatLiveSessionTime(endTime)}`
													: `${formatLiveSessionDate(startTime)} ${formatLiveSessionTime(startTime)} to ${formatLiveSessionTime(endTime)}`}
											</td>
										</tr>
									);
								})}
							</table>
						</div>
					)}

					<div
						style={{
							width: isMobileLayout ? "100%" : undefined,
							maxWidth: isMobileLayout ? "100%" : undefined,
						}}
					>
						<div
							className={styles.confirmationRow}
							style={{
								alignItems: isMobileLayout ? "end" : undefined,
							}}
						>
							<Typography
								style={{
									flexBasis: isMobileLayout ? undefined : "35%",
								}}
								className={styles.sectionTitle}
							>
								{i18nPackage.t("editQuizPage.confirmationStep.platform")}
							</Typography>
							{!enforcedProfilesOnly && (
								<div style={{ alignItems: "start" }}>
									<EditButton targetPageName="Platform"></EditButton>
								</div>
							)}
						</div>

						<hr className={styles.divider} />
						<div className={styles.confirmationPageDetail}>
							<Typography>
								{getPlatformTextFromProctoringOptions(
									enforceConfiguration(
										DEFAULT_PROCTORING_CONFIGURATION_OPTIONS,
										option,
										verityInstituteSettings,
										optionRelatedInstituteSettings,
									).proctoringOptions,
								)}
							</Typography>
						</div>
					</div>
					<div
						style={{
							width: isMobileLayout ? "100%" : undefined,
							maxWidth: isMobileLayout ? "100%" : undefined,
						}}
					>
						<div
							className={styles.confirmationRow}
							style={{
								alignItems: isMobileLayout ? "end" : undefined,
							}}
						>
							<Typography
								style={{
									flexBasis: isMobileLayout ? undefined : "35%",
								}}
								className={styles.sectionTitle}
							>
								{i18nPackage.t("editQuizPage.confirmationStep.configurations")}
							</Typography>
							{!enforcedProfilesOnly && (
								<div style={{ display: "flex", justifyContent: "end" }}>
									<EditButton targetPageName="Configuration" />
								</div>
							)}
						</div>
						<hr className={styles.divider} />
						{Object.keys(optionCategories).length > 0 ? (
							<ConfigurationSummaryList
								optionCategories={optionCategories}
								optionChildren={optionChildren}
								applications={applications}
								extensions={extensions}
							/>
						) : (
							<div className={styles.confirmationPageDetail} style={{ paddingTop: "10px" }}>
								{i18nPackage.t("editQuizPage.confirmationStep.noRestrictions")}
							</div>
						)}
					</div>
					<div
						style={{
							width: isMobileLayout ? "100%" : undefined,
							maxWidth: isMobileLayout ? "100%" : undefined,
						}}
					>
						<div
							style={{
								alignItems: isMobileLayout ? "end" : undefined,
							}}
							className={styles.confirmationRow}
						>
							<Typography
								style={{
									flexBasis: isMobileLayout ? undefined : "35%",
								}}
								className={styles.sectionTitle}
							>
								{i18nPackage.t("editQuizPage.confirmationStep.accommodations")}
							</Typography>
							{!enforcedProfilesOnly && (
								<div style={{ alignItems: "start" }}>
									<EditButton targetPageName="Accommodations"></EditButton>
								</div>
							)}
						</div>

						<hr className={styles.divider} />

						<div className={styles.confirmationRow}>
							<div className={styles.exemptStudentInfo}>
								<span>
									{exemptionCount > 0 ? (
										<>
											<span className="exempt_count">
												<b>{exemptionCount}</b>
											</span>{" "}
											{i18nPackage.t("editQuizPage.confirmationStep.testTakerAccommodated", { count: exemptionCount })}
										</>
									) : (
										<>{i18nPackage.t("editQuizPage.confirmationStep.noAccommodations")}</>
									)}
								</span>
							</div>
						</div>
					</div>
				</ConditionalWrapper>
			)}
		</ConditionalWrapper>
	);
}
